// Winner selection from this activation's completed registrations.
//
// COMPANY-WIDE, NOT A GAME PANEL. The pool is the People list — anyone who
// finished registration — on any activation running a chance-to-win. Ride
// scores are not a ticket. The service shuffles; this page displays.
//
// ADVERTISED TO NOVA ADMIN AND NOT REFUSED HERE, on People's reasoning: the
// service answers a client viewer 403 in its own words, and a local copy of
// that rule is a console that will disagree with it.
//
// THE DESIGN'S PAGE (TASKS §143, step 5): its head and skeleton while the pool
// is read, the kit's Failed with "Ask again" when it is not, and the winner
// count as the design's input, which here had no class at all.

import { NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { Empty, SecLabel, Seg } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import { useEffect, useState, type ReactNode } from "react";
import {
  readDraws,
  runDraw,
  type DrawDesk,
  type DrawPerson,
  type DrawPlacement,
  type DrawRecord,
} from "../api/draw.js";
import type { ApiOptions, Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { ReadFailed } from "./ReadFailed.js";
import { useShow } from "./Show.js";

export type DrawProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onOpenPerson: (attendeeRef: string) => void;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type Show =
  | { readonly phase: "reading" }
  | { readonly phase: "ready"; readonly desk: DrawDesk }
  | { readonly phase: "failed"; readonly failure: Failure };

const PLACEMENT_LABEL: Readonly<Record<DrawPlacement, string>> = {
  same: "Same place",
  order: "In order",
};

const LIST_LEDE =
  "Completed registrations at this activation, the same people as People. This records a selection. It does not email anyone.";

export function Draw({
  api,
  token,
  activationId,
  onOpenPerson,
  onSessionEnded,
  role,
  onEnrol,
}: DrawProps): ReactNode {
  const frame = useShow();
  const [show, setShow] = useState<Show>({ phase: "reading" });
  // Bumped by "Ask again"; the effect below reads again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  const [winnerCount, setWinnerCount] = useState("");
  const [placement, setPlacement] = useState<DrawPlacement>("same");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ readonly kind: "good" | "bad"; readonly detail: string } | null>(
    null,
  );

  useEffect(() => {
    // The frame's directory read goes first. People and Ride wait the same way:
    // two reads started in one tick arrive in whatever order the network
    // settles them, and a recorder that answers in order then hands this desk
    // the activation directory.
    if (frame.status !== "ready") return;

    let cancelled = false;
    setShow({ phase: "reading" });
    setNote(null);
    void (async () => {
      const result = await readDraws(api, token, activationId);
      if (cancelled) return;
      if (!result.ok) {
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        setShow({ phase: "failed", failure: result });
        return;
      }
      setShow({ phase: "ready", desk: result.value });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, frame.status, attempt]);

  // "Ask again" asks again for whatever failed: the show's own read, which the
  // pool waits on, or the pool itself.
  const failure = frame.status === "failed" ? frame.failure : show.phase === "failed" ? show.failure : null;
  if (failure !== null) {
    return (
      <div className="stack">
        <header className="head">
          <h1>Could not load the draw</h1>
        </header>
        <ReadFailed
          what="The service did not answer this desk"
          failure={failure}
          role={role}
          onEnrol={onEnrol}
          notCounted={
            frame.status === "failed"
              ? "No winners were read, and none were picked, because the activation did not load."
              : "No winners were read, and none were picked, because the service did not answer this desk."
          }
          onRetry={() => (frame.status === "failed" ? frame.reload() : setAttempt((n) => n + 1))}
        />
      </div>
    );
  }

  if (show.phase !== "ready") {
    return (
      <div className="stack">
        <PageHead title="Draw" lede="Loading the registration pool…" uuid={activationId} />
        <Loading rows={4} cols={2} />
      </div>
    );
  }

  const desk = show.desk;
  const asked = Number.parseInt(winnerCount, 10);
  const countIsWhole = winnerCount !== "" && String(asked) === winnerCount && asked >= 1;
  const canRun = !busy && countIsWhole && asked <= desk.pool.size && desk.pool.size > 0;

  const onRun = async () => {
    if (!countIsWhole) return;
    setBusy(true);
    setNote(null);
    const result = await runDraw(api, token, {
      activationId,
      winnerCount: asked,
      placement,
    });
    setBusy(false);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    setShow({ phase: "ready", desk: result.value });
    setNote({ kind: "good", detail: "Recorded. Nobody has been emailed." });
  };

  return (
    <div className="stack">
      <PageHead title="Draw" lede={LIST_LEDE} uuid={activationId} explain="info" />

      <section>
        {/* The service's note on when the pool was read, behind the heading's
            (i) as the October hand-off has it. Still the service's sentence,
            not the hand-off's: it is the one that knows when it read. */}
        <SecLabel info={desk.pool.note}>Pool</SecLabel>
        <div className="panel panel-stack">
          <p className="val">
            {desk.pool.size === 0
              ? "Nobody has completed a registration here."
              : `${desk.pool.size} completed registration${desk.pool.size === 1 ? "" : "s"}.`}
          </p>
          {desk.pool.people.length === 0 ? null : (
            <PeopleTable people={desk.pool.people} onOpenPerson={onOpenPerson} />
          )}
        </div>
      </section>

      <section>
        <SecLabel info="Running a live prize is still the client's promoter question. This page only records who the service picked.">
          New draw
        </SecLabel>
        <div className="panel panel-stack">
          <label className="field" style={{ maxWidth: 220 }}>
            <span className="label">How many winners</span>
            <input
              className="input"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={winnerCount}
              disabled={busy || desk.pool.size === 0}
              onChange={(event) => setWinnerCount(event.target.value)}
            />
          </label>
          <Seg
            label="How they place"
            value={PLACEMENT_LABEL[placement]}
            options={[PLACEMENT_LABEL.same, PLACEMENT_LABEL.order]}
            onPick={(label) => setPlacement(label === PLACEMENT_LABEL.order ? "order" : "same")}
          />
          <div className="acts acts-flush">
            <button type="button" className="btn" disabled={!canRun} onClick={() => void onRun()}>
              {busy ? "Drawing…" : "Run draw"}
            </button>
          </div>
        </div>
      </section>

      {note === null ? null : (
        <NoticeBox kind={note.kind} title={note.kind === "good" ? "Draw recorded" : "Draw not recorded"}>
          <p>{note.detail}</p>
        </NoticeBox>
      )}

      <section>
        <SecLabel>History</SecLabel>
        {desk.draws.length === 0 ? (
          <Empty title="No draw has been run here">
            <p>Earlier draws stay when you run another. Nothing is overwritten.</p>
          </Empty>
        ) : (
          <div className="panel panel-stack">
            {desk.draws.map((draw) => (
              <PastDraw key={draw.drawId} draw={draw} onOpenPerson={onOpenPerson} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PastDraw({
  draw,
  onOpenPerson,
}: {
  readonly draw: DrawRecord;
  readonly onOpenPerson: (attendeeRef: string) => void;
}): ReactNode {
  const ranked = draw.placement === "order";
  return (
    <div className="panel-stack">
      <p className="val">
        {PLACEMENT_LABEL[draw.placement]}
        {" · "}
        {when(draw.drawnAt)}
        {draw.drawnBy === null ? null : ` · ${draw.drawnBy}`}
      </p>
      <p className="help help-tight">
        {draw.winnerCount} of {draw.poolSize}
        {" · "}
        <code>{draw.seed}</code>
      </p>
      <div className="tablewrap">
        <div className="tablescroll">
          <table>
            <thead>
              <tr>
                {ranked ? <th>Place</th> : null}
                <th>Person</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {draw.winners.map((winner) => (
                <tr key={winner.attendeeRef}>
                  {ranked ? (
                    <td data-label="Place" className="num">
                      {winner.place ?? ""}
                    </td>
                  ) : null}
                  <PersonCells person={winner} onOpenPerson={onOpenPerson} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PeopleTable({
  people,
  onOpenPerson,
}: {
  readonly people: readonly DrawPerson[];
  readonly onOpenPerson: (attendeeRef: string) => void;
}): ReactNode {
  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {people.map((person) => (
              <tr key={person.attendeeRef}>
                <PersonCells person={person} onOpenPerson={onOpenPerson} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PersonCells({
  person,
  onOpenPerson,
}: {
  readonly person: DrawPerson;
  readonly onOpenPerson: (attendeeRef: string) => void;
}): ReactNode {
  return (
    <>
      <td data-label="Person">
        <button type="button" className="linkish" onClick={() => onOpenPerson(person.attendeeRef)}>
          {person.displayName ?? "No name given"}
        </button>
      </td>
      <td data-label="Email">{person.contactEmail ?? "no email given"}</td>
    </>
  );
}

function when(value: string): string {
  // The service's own clock, not the browser's zone. Converting would invent
  // hour digits that are not in the answer this page is not allowed to compute.
  return /T/.test(value) ? `${value.replace("T", " ").replace(/Z$/, "")} UTC` : value;
}
